import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import Table from "../../../core/common/dataTable/index";
import Preloader from "../../../core/common/loader/preLoader";
import { DateFormat } from "../../../core/hooks/dateFormat";
import Api from "../../../core/data/redux/utils/apiCall";
import { all_routes } from "../../router/all_routes";
import PermissionGuard from "../../permissionGuard";
import { isSupportViewer } from "../../../utils/permissionUtils";
import {
  ApplicationModule,
  ApproverRecord,
  EmployeeOption,
  listApplicationModules,
  listApprovers,
  padEmpId,
  saveApprover,
  searchEmployees,
  toggleApproverStatus,
} from "./eAttestation.service";

const routes = all_routes;
const PRIMARY = "#c0282c";

function normalizeModuleKey(value: string): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

function sanitizeLevel(value: any): number | undefined {
  const digit = String(value ?? "").replace(/[^1-9]/g, "").slice(0, 1);
  return digit ? Number(digit) : undefined;
}

const EAttestationApprover: React.FC = () => {
  const [form] = Form.useForm();
  const [records, setRecords] = useState<ApproverRecord[]>([]);
  const [modules, setModules] = useState<ApplicationModule[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchingEmployees, setSearchingEmployees] = useState(false);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ApproverRecord | null>(null);
  const [searchText, setSearchText] = useState("");
  const [moduleFilter, setModuleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [togglingIds, setTogglingIds] = useState<Record<number, boolean>>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchModules = useCallback(async () => {
    setModulesLoading(true);
    try {
      const list = await listApplicationModules();
      setModules(Array.isArray(list) ? list : []);
    } catch (error: any) {
      toast.error(Api.getErrorMessage(error) || "Failed to load modules");
      setModules([]);
    } finally {
      setModulesLoading(false);
    }
  }, []);

  const fetchRecords = useCallback(async (moduleName = moduleFilter) => {
    setLoading(true);
    try {
      const list = await listApprovers({
        moduleName: moduleName || undefined,
      });
      setRecords(Array.isArray(list) ? list : []);
    } catch (error: any) {
      toast.error(Api.getErrorMessage(error) || "Failed to load approvers");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [moduleFilter]);

  useEffect(() => {
    fetchModules();
    fetchRecords("");
  }, []);

  const moduleOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string; slug?: string }[] = [];

    const pushUnique = (value: string, label?: string, slug?: string) => {
      const display = String(label || value || "").trim();
      const raw = String(value || display).trim();
      if (!raw && !display) return;
      const key = normalizeModuleKey(display) || normalizeModuleKey(raw) || normalizeModuleKey(slug || "");
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push({
        value: display || raw,
        label: display || raw,
        slug: slug || raw,
      });
    };

    modules.forEach((item) => pushUnique(item.name, item.name, item.slug));
    records.forEach((row) => pushUnique(row.moduleName, row.moduleName, row.moduleName));

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [modules, records]);

  const resolveModuleValue = useCallback(
    (moduleName?: string) => {
      const key = normalizeModuleKey(moduleName || "");
      if (!key) return undefined;
      const match = moduleOptions.find(
        (item) =>
          normalizeModuleKey(item.value) === key ||
          normalizeModuleKey(item.label) === key ||
          normalizeModuleKey(item.slug || "") === key
      );
      return match?.value || String(moduleName || "").trim();
    },
    [moduleOptions]
  );

  const filteredRecords = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return records.filter((row) => {
      if (statusFilter === "active" && !row.is_active) return false;
      if (statusFilter === "inactive" && row.is_active) return false;
      if (!query) return true;
      return [row.name, row.empId, row.designation, row.email, row.moduleName, row.level]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query));
    });
  }, [records, searchText, statusFilter]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: filteredRecords.length,
    }));
  }, [filteredRecords.length, searchText, statusFilter, moduleFilter]);

  const runEmployeeSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        const query = String(value || "").trim();
        if (query.length < 2) {
          setEmployeeOptions([]);
          setSearchingEmployees(false);
          return;
        }
        setSearchingEmployees(true);
        try {
          const list = await searchEmployees(query);
          setEmployeeOptions(list);
        } catch (error: any) {
          toast.error(Api.getErrorMessage(error) || "Failed to search employees");
          setEmployeeOptions([]);
        } finally {
          setSearchingEmployees(false);
        }
      }, 400),
    []
  );

  useEffect(() => {
    return () => {
      runEmployeeSearch.cancel();
    };
  }, [runEmployeeSearch]);

  const applyEmployee = (employee?: EmployeeOption) => {
    if (!employee) return;
    form.setFieldsValue({
      empId: employee.empId,
      name: employee.name,
      email: employee.email,
      designation: employee.designation,
    });
  };

  const openCreate = () => {
    setEditingRecord(null);
    setEmployeeOptions([]);
    form.resetFields();
    form.setFieldsValue({
      moduleName: undefined,
      empId: undefined,
      name: "",
      email: "",
      designation: "",
      level: 1,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (record: ApproverRecord) => {
    setEditingRecord(record);
    const currentEmployee: EmployeeOption = {
      empId: padEmpId(record.empId),
      name: record.name,
      email: record.email,
      designation: record.designation,
    };
    setEmployeeOptions([currentEmployee]);
    form.setFieldsValue({
      moduleName: resolveModuleValue(record.moduleName),
      empId: currentEmployee.empId,
      name: record.name,
      email: record.email,
      designation: record.designation,
      level: sanitizeLevel(record.level) ?? 1,
      is_active: !!record.is_active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
    setEmployeeOptions([]);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const level = Number(values.level);
      if (!Number.isInteger(level) || level < 1 || level > 9) {
        toast.error("Level must be between 1 and 9");
        return;
      }

      const payload: Partial<ApproverRecord> = {
        moduleName: resolveModuleValue(values.moduleName) || String(values.moduleName || "").trim(),
        empId: padEmpId(values.empId),
        designation: String(values.designation || "").trim(),
        name: String(values.name || "").trim(),
        email: String(values.email || "").trim(),
        level,
        is_active: values.is_active !== false,
      };
      if (editingRecord?.id) payload.id = editingRecord.id;

      setSaving(true);
      const result = await saveApprover(payload);
      if (result.success) {
        toast.success(result.message);
        closeModal();
        await fetchRecords(moduleFilter);
      } else {
        toast.error(result.message || "Unable to save approver");
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      toast.error(Api.getErrorMessage(error) || "Unable to save approver");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (record: ApproverRecord, checked: boolean) => {
    if (!record.id || isSupportViewer()) return;
    setTogglingIds((prev) => ({ ...prev, [record.id as number]: true }));
    setRecords((prev) =>
      prev.map((row) =>
        row.id === record.id ? { ...row, is_active: checked } : row
      )
    );
    try {
      const result = await toggleApproverStatus(record.id, checked);
      if (result.success) {
        toast.success(result.message);
        if (result.data) {
          setRecords((prev) =>
            prev.map((row) =>
              row.id === record.id ? { ...row, ...result.data } : row
            )
          );
        }
      } else {
        throw new Error(result.message);
      }
    } catch (error: any) {
      setRecords((prev) =>
        prev.map((row) =>
          row.id === record.id ? { ...row, is_active: record.is_active } : row
        )
      );
      toast.error(Api.getErrorMessage(error) || "Failed to update status");
    } finally {
      setTogglingIds((prev) => {
        const next = { ...prev };
        delete next[record.id as number];
        return next;
      });
    }
  };

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (_: any, __: ApproverRecord, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: ApproverRecord, b: ApproverRecord) =>
        String(a.name || "").localeCompare(String(b.name || "")),
    },
    {
      title: "Emp ID",
      dataIndex: "empId",
      render: (value: string) => padEmpId(value) || "-",
      sorter: (a: ApproverRecord, b: ApproverRecord) =>
        String(a.empId || "").localeCompare(String(b.empId || "")),
    },
    {
      title: "Designation",
      dataIndex: "designation",
      sorter: (a: ApproverRecord, b: ApproverRecord) =>
        String(a.designation || "").localeCompare(String(b.designation || "")),
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Module",
      dataIndex: "moduleName",
    },
    {
      title: "Level",
      dataIndex: "level",
      width: 90,
      sorter: (a: ApproverRecord, b: ApproverRecord) =>
        Number(a.level || 0) - Number(b.level || 0),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 140,
      render: (_: boolean, record: ApproverRecord) => (
        <Switch
          checked={!!record.is_active}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          loading={!!togglingIds[record.id as number]}
          onChange={(checked) => handleToggle(record, checked)}
          disabled={isSupportViewer()}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      render: (value: string) => DateFormat(value) || "-",
    },
    ...(!isSupportViewer()
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            width: 90,
            render: (_: any, record: ApproverRecord) => (
              <Space>
                <PermissionGuard route="e-attestation/approver">
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => openEdit(record)}
                  />
                </PermissionGuard>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="page-wrapper">
      {loading && !records.length ? <Preloader /> : null}
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">E-Attestation Approver</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Administration</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  E-Attestation Approver
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <PermissionGuard route="e-attestation/approver">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreate}
                style={{
                  backgroundColor: PRIMARY,
                  border: `1px solid ${PRIMARY}`,
                  fontWeight: 500,
                }}
              >
                Add Approver
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <div className="px-3 pb-2 d-flex flex-wrap gap-2">
              <Select
                allowClear
                showSearch
                loading={modulesLoading}
                placeholder="Filter by module"
                value={moduleFilter || undefined}
                style={{ minWidth: 280 }}
                optionFilterProp="label"
                options={moduleOptions}
                onChange={(value) => {
                  const next = value || "";
                  setModuleFilter(next);
                  fetchRecords(next);
                }}
              />
              <Select
                value={statusFilter}
                style={{ minWidth: 150 }}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                onChange={setStatusFilter}
              />
            </div>
            <Table
              columns={columns}
              dataSource={filteredRecords}
              Selection={false}
              paginationConfig={{
                ...pagination,
                total: filteredRecords.length,
              }}
              handlePagination={(next: any) =>
                setPagination((prev) => ({
                  ...prev,
                  current: next.current || 1,
                  pageSize: next.pageSize || prev.pageSize,
                  total: filteredRecords.length,
                }))
              }
              loading={loading}
              handleSearch={setSearchText}
              searchValue={searchText}
              searchPlaceholder="Search name, emp id, email or module"
            />
          </div>
        </div>

        <Modal
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i className={`ti ti-${editingRecord ? "edit" : "plus"}`} />
              {editingRecord ? "Edit Approver" : "Assign Approver"}
            </div>
          }
          open={modalOpen}
          onOk={handleSave}
          onCancel={closeModal}
          confirmLoading={saving}
          okText={editingRecord ? "Update" : "Assign"}
          cancelText="Cancel"
          destroyOnClose
          width={720}
        >
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="moduleName"
                  label="Module Name"
                  rules={[{ required: true, message: "Please select module" }]}
                >
                  <Select
                    showSearch
                    loading={modulesLoading}
                    placeholder="Select module"
                    optionFilterProp="label"
                    options={moduleOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="empId"
                  label="Employee"
                  rules={[{ required: true, message: "Please select employee" }]}
                  extra="Search by employee ID or name. Name, email and designation will auto-fill."
                >
                  <Select
                    showSearch
                    filterOption={false}
                    placeholder="Search employee by ID or name"
                    notFoundContent={
                      searchingEmployees ? <Spin size="small" /> : "No employee found"
                    }
                    onSearch={runEmployeeSearch}
                    onChange={(value) => {
                      const selected = employeeOptions.find(
                        (item) => item.empId === value
                      );
                      applyEmployee(selected);
                    }}
                    options={employeeOptions.map((item) => ({
                      value: item.empId,
                      label: `${item.empId} - ${item.name || "Unknown"}`,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: "Name is required" }]}
                >
                  <Input placeholder="Employee name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="designation"
                  label="Designation"
                  rules={[{ required: true, message: "Designation is required" }]}
                >
                  <Input placeholder="Designation" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input placeholder="Employee email" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="level"
                  label="Level"
                  extra="Only 1 to 9 allowed"
                  getValueFromEvent={(event) => {
                    const raw =
                      event && typeof event === "object" && "target" in event
                        ? event.target.value
                        : event;
                    const digit = String(raw ?? "").replace(/[^1-9]/g, "").slice(0, 1);
                    return digit ? Number(digit) : undefined;
                  }}
                  rules={[
                    { required: true, message: "Level is required" },
                    {
                      validator: async (_, value) => {
                        const level = Number(value);
                        if (!Number.isInteger(level) || level < 1 || level > 9) {
                          return Promise.reject(
                            new Error("Level must be between 1 and 9")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    maxLength={1}
                    inputMode="numeric"
                    pattern="[1-9]"
                    placeholder="1-9"
                    onKeyDown={(event) => {
                      const allowedKeys = [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Home",
                        "End",
                      ];
                      if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
                        return;
                      }
                      if (!/^[1-9]$/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      const text = event.clipboardData.getData("text").trim();
                      if (!/^[1-9]$/.test(text)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="is_active"
                  label="Status"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default EAttestationApprover;
